import React, {Fragment, useMemo, useState} from "react";
import {Button, Descriptions, Tooltip, Typography} from "antd";
import {getModelHumanReadable, getBundleIdAsDate} from "utils/helpers";
import {useTranslation} from "react-i18next";
import {CourierSummarDeviceHistoryModalInformation} from "../courierSummarDeviceHistoryModalInformation/CourierSummarDeviceHistoryModalInformation";
import {UnorderedListOutlined, InfoCircleOutlined} from "@ant-design/icons";
import moment from "moment";
import {VFlex} from "components/_defaultComponents/Flex/VFlex";
import {VText} from "components/_defaultComponents/Text/VText";

const nfcValuePrefix = "fields.deviceNfc";
const notificationPermissionValuePrefix = "fields.deviceNotificationPermission";
const alwaysLocationPermissionValuePrefix = "fields.deviceAlwaysLocationPermission";
const firebaseInitializedValuePrefix = "fields.deviceFirebaseInitialized";
const permissionGrantedValuePrefix = "fields.devicePermissionGranted";
const deviceRegisteredValuePrefix = "fields.deviceRegistered";
const tokenReceivedValuePrefix = "fields.deviceTokenReceived";

export const CourierSummaryDeviceInformation = ({courierId, deviceInfo}) => {
    const {t} = useTranslation(["courierDetail"]);
    const [deviceInfoHistoryModalVisible, setDeviceInfoHistoryModalVisible] = useState(false);

    const checkBundleDate = bundleId => {
        const bundleDate = getBundleIdAsDate(bundleId, true);
        return bundleDate ? bundleDate : t("fields.nonValidBundle");
    };

    const getValuePrefix = (value, prefix) => {
        return value === true
            ? t(`${prefix}.yes`)
            : value === false
            ? t(`${prefix}.no`)
            : t(`${prefix}.unknown`);
    };

    const descriptionItems = useMemo(() => {
        const humanReadableModel = getModelHumanReadable(deviceInfo?.model, deviceInfo?.os);

        const notificationPermissionValue = getValuePrefix(
            deviceInfo?.notificationPermission,
            notificationPermissionValuePrefix
        );
        const alwaysLocationPermissionValue = getValuePrefix(
            deviceInfo?.alwaysLocationPermission,
            alwaysLocationPermissionValuePrefix
        );
        const firebaseInitializedValue = getValuePrefix(
            deviceInfo?.firebaseInitialized,
            firebaseInitializedValuePrefix
        );
        const permissionGrantedValue = getValuePrefix(
            deviceInfo?.permissionGranted,
            permissionGrantedValuePrefix
        );
        const deviceRegisteredValue = getValuePrefix(
            deviceInfo?.deviceRegistered,
            deviceRegisteredValuePrefix
        );
        const tokenReceivedValue = getValuePrefix(
            deviceInfo?.tokenReceived,
            tokenReceivedValuePrefix
        );

        const playServicesValue =
            deviceInfo?.playServicesAvailable === null ||
            deviceInfo?.playServicesAvailable === undefined
                ? t("fields.devicePlayServicesAvailable.notApplicable")
                : deviceInfo?.playServicesAvailable
                ? t("fields.devicePlayServicesAvailable.yes")
                : t("fields.devicePlayServicesAvailable.no");

        const apnsTokenValue =
            deviceInfo?.apnsTokenAvailable === null || deviceInfo?.apnsTokenAvailable === undefined
                ? t("fields.deviceApnsTokenAvailable.notApplicable")
                : deviceInfo?.apnsTokenAvailable
                ? t("fields.deviceApnsTokenAvailable.yes")
                : t("fields.deviceApnsTokenAvailable.no");

        const firstBundleDate = getBundleIdAsDate(deviceInfo?.minBundleId);
        const currentBundleDate = getBundleIdAsDate(deviceInfo?.bundleId);

        const firstBundleDateTooltipTitle = checkBundleDate(deviceInfo?.minBundleId);
        const currentBundleDateTooltipTitle = checkBundleDate(deviceInfo?.bundleId);

        return [
            {
                label: t("fields.deviceBrand"),
                value: deviceInfo?.brand || "-"
            },
            {
                label: t("fields.deviceModel"),
                value: humanReadableModel || "-"
            },
            {
                label: t("fields.deviceOperatingSystem"),
                value: deviceInfo?.os || "-"
            },
            {
                label: t("fields.deviceOperatingSystemVersion"),
                value: deviceInfo?.osVersion || "-"
            },
            {
                label: t("fields.versionName"),
                value: deviceInfo?.version || "-"
            },
            {
                label: () => {
                    return (
                        <Tooltip title={t("fields.deviceHistoryTooltip.currentBundle.label")}>
                            {t("fields.currentBundleId")} <InfoCircleOutlined />
                        </Tooltip>
                    );
                },
                value: () => {
                    return (
                        <VFlex vertical>
                            <VFlex>
                                <Tooltip title={currentBundleDateTooltipTitle}>
                                    <VFlex align="center" gap={4}>
                                        <VText>
                                            {currentBundleDate
                                                ? moment(currentBundleDate).fromNow()
                                                : t("fields.nonValidBundle")}
                                        </VText>
                                        <InfoCircleOutlined />
                                    </VFlex>
                                </Tooltip>
                            </VFlex>
                            <VText fontSize={10}>
                                {t("fields.deviceHistoryTooltip.currentBundle.description")}
                            </VText>
                        </VFlex>
                    );
                }
            },
            {
                label: () => {
                    return (
                        <Tooltip title={t("fields.deviceHistoryTooltip.firstBundle.label")}>
                            {t("fields.firstBundleId")} <InfoCircleOutlined />
                        </Tooltip>
                    );
                },
                value: () => {
                    return (
                        <VFlex vertical>
                            <VFlex>
                                <Tooltip title={firstBundleDateTooltipTitle}>
                                    <VFlex align="center" gap={4}>
                                        <VText>
                                            {firstBundleDate
                                                ? moment(firstBundleDate).fromNow()
                                                : t("fields.nonValidBundle")}
                                        </VText>
                                        <InfoCircleOutlined />
                                    </VFlex>
                                </Tooltip>
                            </VFlex>
                            <VText fontSize={10}>
                                {t("fields.deviceHistoryTooltip.firstBundle.description")}
                            </VText>
                        </VFlex>
                    );
                }
            },
            {
                label: t("fields.deviceNotificationPermission.title"),
                value: notificationPermissionValue
            },
            {
                label: t("fields.deviceAlwaysLocationPermission.title"),
                value: alwaysLocationPermissionValue
            },
            {
                label: t("fields.deviceFirebaseInitialized.title"),
                value: firebaseInitializedValue
            },
            {
                label: t("fields.devicePermissionGranted.title"),
                value: permissionGrantedValue
            },
            {
                label: t("fields.deviceRegistered.title"),
                value: deviceRegisteredValue
            },
            {
                label: t("fields.deviceTokenReceived.title"),
                value: tokenReceivedValue
            },
            {
                label: t("fields.devicePlayServicesAvailable.title"),
                value: playServicesValue
            },
            {
                label: t("fields.devicePlayServicesVersion.title"),
                value:
                    deviceInfo?.playServicesVersion ??
                    t("fields.devicePlayServicesVersion.notApplicable")
            },
            {
                label: t("fields.deviceApnsTokenAvailable.title"),
                value: apnsTokenValue
            },
            {
                label: t("fields.deviceFcmToken.title"),
                value: () => {
                    if (!deviceInfo?.fcmToken) {
                        return t("fields.deviceFcmToken.empty");
                    }
                    const token = deviceInfo.fcmToken;
                    const shortened = `${token.substring(0, 8)}...${token.substring(
                        token.length - 8
                    )}`;
                    return (
                        <Tooltip title={token}>
                            <Typography.Text copyable={{text: token}}>{shortened}</Typography.Text>
                        </Tooltip>
                    );
                }
            },
            {
                label: t("fields.deviceHistory.title"),
                value: () => (
                    <Button
                        type="primary"
                        onClick={() => setDeviceInfoHistoryModalVisible(true)}
                        icon={<UnorderedListOutlined />}
                    >
                        {t("fields.deviceHistory.view")}
                    </Button>
                )
            }
        ];
    }, [deviceInfo, t]);

    return (
        <Fragment>
            <Descriptions bordered size={"small"} column={1} className="courierSummaryDescriptions">
                {descriptionItems.map(item => (
                    <Descriptions.Item
                        key={item.label}
                        label={typeof item.label === "function" ? item.label() : item.label}
                    >
                        {typeof item.value === "function" ? item.value() : item.value}
                    </Descriptions.Item>
                ))}
            </Descriptions>
            <CourierSummarDeviceHistoryModalInformation
                courierId={courierId}
                title={t("fields.deviceHistory.title")}
                visible={deviceInfoHistoryModalVisible}
                onClose={() => setDeviceInfoHistoryModalVisible(false)}
            />
        </Fragment>
    );
};
